import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { EdifyContentUnitType } from "constants/edify-constants";
import type { ContentAdapterAPI } from "generated/types";

import { ImmersiveReaderButton } from "components/ImmersiveReaderButton/ImmersiveReaderButton";
import { StatusTag } from "components/StatusTag/StatusTag";

import { Box } from "styles/helpers/layout";
import {
  StyledCard,
  StyledHeader,
  StyledTopicListItem,
} from "../../../WritingSubjectDetailsPage/components/ChapterList/ChapterList.styled";
import { EdifyTheoryButton } from "../EdifyTheoryButton/EdifyTheoryButton";

interface EdifyChapterBoxProps {
  chapter: ContentAdapterAPI.EdifyChapterListItem;
  buildEdifyTopicDetailsUrl: (topicId: string) => string;
}

export const EdifyChapterBox: React.FC<EdifyChapterBoxProps> = ({
  chapter,
  buildEdifyTopicDetailsUrl,
}) => {
  const { t } = useTranslation("homepage");

  const theoryTopics = useMemo(
    () => chapter.topics.filter((topic) => topic.type === EdifyContentUnitType.Theory),
    [chapter],
  );

  const practiceTopics = useMemo(
    () => chapter.topics.filter((topic) => topic.type !== EdifyContentUnitType.Theory),
    [chapter],
  );

  return (
    <StyledCard
      key={chapter.id}
      data-cy="edify-chapter-list-item"
      data-ir-target={`edify-chapter-${chapter.id}`}
    >
      <StyledHeader alignItems="center" columnGap={1} justifyContent="space-between">
        <ImmersiveReaderButton
          targetIndex={0}
          targetSelector={`[data-ir-target="edify-chapter-${chapter.id}"]`}
        />
        <span data-cy="edify-chapter-title" style={{ flexGrow: 1 }}>
          {chapter.title}
        </span>
        {/* -- Theory topic list (accessible via button) -- */}
        <EdifyTheoryButton
          theoryTopics={theoryTopics}
          buildEdifyTopicDetailsUrl={buildEdifyTopicDetailsUrl}
        />
      </StyledHeader>
      {/* -- Practice topic list -- */}
      <Box>
        {practiceTopics.map((topic) => (
          <StyledTopicListItem
            key={topic.id}
            dataCy="edify-practice-topic-list-item"
            href={buildEdifyTopicDetailsUrl(topic.id)}
            title={topic.title}
            extraData={
              !!topic.estimatedDuration && (
                <StatusTag
                  dataCy="edify-topic-list-item-duration"
                  icon="far fa-clock"
                  label={t("edifySubjectDetails.topicListItem.estimatedDuration", {
                    count: topic.estimatedDuration,
                  })}
                />
              )
            }
          />
        ))}
      </Box>
    </StyledCard>
  );
};
